/**
 * @fileoverview Browser push-notification support and permission helpers.
 * @description Feature-detects whether the current browser can receive push notifications at all,
 * and wraps the native permission prompt, so callers never touch the Notification/serviceWorker
 * globals directly.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

/**
 * @description Checks whether the current browser supports the APIs push notifications need
 * (the Notification API and Service Workers).
 * @returns {boolean} True when push notifications are supported.
 */
export function isPushNotificationSupported(): boolean {
  return typeof window !== 'undefined' && 'Notification' in window && 'serviceWorker' in navigator
}

/**
 * @description Prompts the user for notification permission. Resolves to "denied" without
 * prompting when push notifications aren't supported, rather than throwing.
 * @returns {Promise<NotificationPermission>} The resulting permission state.
 */
export async function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!isPushNotificationSupported()) {
    return 'denied'
  }

  return Notification.requestPermission()
}
